import { createSelector, createSlice } from "@reduxjs/toolkit";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { logout } from "../../common/reducers/initContext";
import { createMapSelector, dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, initMultiMapFactory, queryMapFactory } from "../../common/reducers/utilities";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { ensureInteger } from "../../common/utils/generalUtils";
import { deepEquals } from "../../common/utils/objectUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = roster => roster.rosterid;

const updateState = (state, roster) => {
    if (!state.data[roster.rosterid] || !deepEquals(state.data[roster.rosterid], roster)) {
        state.data[roster.rosterid] = roster;
    }
}

const removeState = (state, rosterid) => {
    delete state.data[rosterid];
}

export const equipmentRosterStaffSlice = createSlice({
    name: 'staffEquipmentRosters',
    initialState: {
        data: {},
    },
    reducers: {
        set: (state, action) => {
            const toProcess = new Set(action.payload.ids.map(id => ensureInteger(id)));
            ensureArray(action.payload.data).forEach(roster => {
                updateState(state, roster);
                toProcess.delete(key(roster));
            })
            toProcess.forEach(id => delete state.data[id]);
        },
        setForShifts: (state, action) => {
            const toRemove = new Set(Object.values(state.data).filter(r => action.payload.shiftids.includes(r.shiftid)).map(r => key(r)));

            action.payload.data.forEach(roster => {
                updateState(state, roster);
                toRemove.delete(key(roster));
            });
            toRemove.forEach(rosterid => removeState(state, rosterid));
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(r => {
                if (!state.data[key(r)] || !deepEquals(state.data[key(r)], r)) {
                    state.data[key(r)] = r;
                }
            });
        },
        updateShift: (state, action) => {
            ensureArray(action.payload).forEach(shift => {
                for (const roster of Object.values(state.data)) {
                    if (roster.shiftid === shift.id) {
                        roster.shift_start = shift.start;
                        roster.shift_end = shift.end;
                    }
                }
            })
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(roster => {
                delete state.data[key(roster)];
            })
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => equipmentRosterStaffSlice.getInitialState());
    }
})

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'staffEquipmentRoster', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'staffEquipmentRoster');

const fetchRoster = aggregatingFetch(
    id => network => network.get('/api/v2/staffEquipment_roster/' + encodeURIComponent(id), data => equipmentRosterStaffSlice.actions.set({ ids: [id], data }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/staffEquipment_rosters', ids, data => equipmentRosterStaffSlice.actions.set({ ids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)

const fetchShiftRosters = aggregatingFetch(
    shiftid => network => network.get('/api/v2/staffEquipmentRostersForShift/' + encodeURIComponent(shiftid), data => equipmentRosterStaffSlice.actions.setForShifts({ shiftids: [shiftid], data }), visualizeFailure('get')),
    shiftids => network => network.post('/api/v2/staffEquipmentRostersForShifts', shiftids, data => equipmentRosterStaffSlice.actions.setForShifts({ shiftids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)

// #endregion

// #region selectors
const selectRawRosters = state => state.staffEquipmentRosters.data;
const selectDataByShift = createMapSelector(
    [selectRawRosters],
    (rosters) => {
        return Object.values(rosters).reduce((res, r) => {
            if (!res[r.shiftid]) {
                res[r.shiftid] = [];
            }
            res[r.shiftid].push(r);
            return res;
        }, {});
    }
);

const selectShiftRosters = (shiftidsInput) => {
    const shiftids = ensureArray(shiftidsInput); // normalize once for consistency
    return createSelector([selectDataByShift], (shiftRosters) => {
        return shiftids.flatMap(shiftid => shiftRosters[shiftid] ?? [])
    });
};

// #endregion

const listen = {
    equipmentRosterStaffCreated: (roster, { dispatch, websocket }) => {
        markFetched(websocket, 'equipment_roster', key(roster))
        dispatch(equipmentRosterStaffSlice.actions.addOrUpdate(roster))
    },
    equipmentRosterStaffUpdated: (roster, { dispatch }) => dispatch(equipmentRosterStaffSlice.actions.addOrUpdate(roster)),
    equipmentRosterStaffRemoved: (roster, { dispatch }) => dispatch(equipmentRosterStaffSlice.actions.remove(roster)),
    shiftStaffUpdated: (shift, { dispatch }) => dispatch(equipmentRosterStaffSlice.actions.updateShift(shift))
}
const options = {
    cleanup: { root: equipmentRosterStaffSlice.name, all: state => Object.values(selectRawRosters(state)), removeAll: equipmentRosterStaffSlice.actions.remove, key },
    condition: credentials => credentials?.hasRole('member') 
}
const rosterInitByID = initMapFactory(fetchRoster, listen, { name: 'rosterByID' });
registerEntityBasedFetch('equipment_roster', rosterInitByID)

const rosterInitByShift = queryMapFactory(fetchShiftRosters, 'rosterid', rosterInitByID, listen);
registerEntityBasedFetch('shift', rosterInitByShift);

export const [useStaffEquipmentRostersForShift] = dataHookWithCredentialsFactory(selectShiftRosters, rosterInitByShift, options);
export const [useStaffEquipmentRostersForShifts] = dataHookWithCredentialsFactory(selectShiftRosters, initMultiMapFactory(rosterInitByShift, listen), options);


const reducer = { name: equipmentRosterStaffSlice.name, reducer: equipmentRosterStaffSlice.reducer }
export default reducer;